<template>
  <div>
    <div class="passwordDoubleCheck" :class="{ isPc: !isSp }">
      <InputLabel
        class="labelArea"
        :class="{ isPc: !isSp, isHasLabel: (!isSp && required) || labelWidthHasLabel, styleLabelWide: styleLabelWide }"
        :required="required"
      >
        {{ areaLabel }}
      </InputLabel>
      <InputItem
        class="inputItem"
        input-type="password"
        placeholder-value="新しいパスワードを入力"
        :class="{ hasError: hasErrorPassword1, isPc: !isSp }"
        :disabled="disabled"
        :input-value="propPassword1"
        :is-new-password="true"
        trim-space-start-end
        @blur="onBlurPassword1"
        @change="onChangePassword1"
        @focus="onFocusPassword1"
      />
    </div>
    <div
      class="underInputArea"
      :class="{ isPc: !isSp, isNoLabelInPc: !isSp && !required && !labelWidthHasLabel, styleLabelWide: styleLabelWide }"
    >
      <InputItem
        class="inputItem"
        input-type="password"
        placeholder-value="確認の為もう一度入力して下さい"
        :class="{ hasError: hasErrorPassword2, isPc: !isSp }"
        :disabled="disabled"
        :input-value="propPassword2"
        :is-new-password="true"
        trim-space-start-end
        @blur="onBlurPassword2"
        @change="onChangePassword2"
        @focus="onFocusPassword2"
      />
      <div class="validationErrorMessageArea">
        <PartialErrorMessage :is-sp-flg="true" :error="displayErrorPassword1" class="partialErrorComponent" />
        <PartialErrorMessage
          :is-sp-flg="true"
          :error="displayErrorPassword2"
          class="partialErrorComponent"
          :class="{ hasTwoError: hasErrorPassword1 }"
        />
      </div>
      <CaptionText
        v-if="captionText"
        class="captionText"
        :class="{ hasError: hasErrorPassword1 || hasErrorPassword2, isPc: !isSp }"
      >
        {{ captionText }}
      </CaptionText>
    </div>
  </div>
</template>

<script>
import InputLabel from '../Text/InputLabel.vue';
import InputItem from './InputItem.vue';
import PartialErrorMessage from '../PartialErrorMessage.vue';
import {
  isFullWidth,
  isHalfWidthKana,
  isIncludeNumber,
  isIncludeEnglish,
  checkWordCount,
} from '../../../../helper/_validationCheck';
import CaptionText from '../Text/CaptionText.vue';
import {
  GENERAL_VALIDATION_MESSAGE,
  SPECIFY_VALIDATION_MESSAGE,
  generateWordCountError,
} from '../../../../helper/_validationCheckMessages';

const PASSWORD_MIN = 6;
const PASSWORD_MAX = 72;

export default {
  name: 'PasswordDoubleCheck',
  components: {
    InputLabel,
    InputItem,
    PartialErrorMessage,
    CaptionText,
  },
  props: {
    areaLabel: {
      type: String,
      required: true,
    },
    /**
     * 外部からのエラー
     * このコンポーネントに定義しないような独自のバリデーションを持つ場合、
     * 親コンポーネントでv-modelの値をwatchとかしてバリデーションチェックを行い、
     * errorFromOuterにpropsで渡すと、最後の項目としてチェック入る。
     * 空文字・undefinedを渡すことで『エラー無し』扱いになる。
     */
    errorFromOuterPassword1: {
      type: String,
      required: false,
      default: '',
    },
    errorFromOuterPassword2: {
      type: String,
      required: false,
      default: '',
    },
    required: {
      type: Boolean,
      required: false,
      default: false,
    },
    requiredWithNoLabel: {
      type: Boolean,
      required: false,
      default: false,
    },
    captionText: {
      type: String,
      required: false,
      default: undefined,
    },
    userName: {
      type: String,
      required: false,
      default: undefined,
    },
    emailAccount: {
      type: String,
      required: false,
      default: undefined,
    },
    emailAddress: {
      type: String,
      required: false,
      default: undefined,
    },
    forceValidationCheckFlg: {
      type: Boolean,
      required: false,
      default: false,
    },
    styleLabelWide: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    isSpFlg: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    labelWidthHasLabel: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    propPassword1: {
      type: String,
      required: false,
      default: '',
    },
    propPassword2: {
      type: String,
      required: false,
      default: '',
    },
    // 先頭と末尾のスペースをトリムするフラグ
    trimSpaceStartEnd: {
      type: Boolean,
      required: false,
      default: undefined,
    },
  },
  data() {
    return {
      validationErrorPassword1: undefined,
      validationErrorPassword2: undefined,
      password1: this.trimSpaceStartEnd ? this.propPassword1 : '',
      password2: this.trimSpaceStartEnd ? this.propPassword2 : '',
    };
  },
  computed: {
    isSp() {
      if (typeof this.isSpFlg === 'boolean') return this.isSpFlg;
      return this.$isSp;
    },
    displayErrorPassword1() {
      if (this.validationErrorPassword1) return this.validationErrorPassword1;
      return this.errorFromOuterPassword1;
    },
    displayErrorPassword2() {
      if (this.validationErrorPassword2) return this.validationErrorPassword2;
      return this.errorFromOuterPassword2;
    },
    hasErrorPassword1() {
      // displayError自体がundefinedならエラーない
      if (!this.displayErrorPassword1) return false;
      // displayError自体がlengthもってるならエラーあり
      if (this.displayErrorPassword1.length) return true;
      return false;
    },
    hasErrorPassword2() {
      // displayError自体がundefinedならエラーない
      if (!this.displayErrorPassword2) return false;
      // displayError自体がlengthもってるならエラーあり
      if (this.displayErrorPassword2.length) return true;
      return false;
    },
    isRequired() {
      return this.required || this.requiredWithNoLabel;
    },
  },
  watch: {
    forceValidationCheckFlg() {
      // user名を変更した時とかにパスワードのバリデーションチェックを強制発火させる
      // ただし、passwordの上欄入力済みのときのみ
      if (this.password1) {
        this.onBlurPassword1();
      }
    },
  },
  methods: {
    onFocusPassword1() {
      this.$emit('focusPassword1');
    },
    onFocusPassword2() {
      this.$emit('focusPassword2');
    },
    onBlurPassword1() {
      this.validationErrorPassword1 = this.validationCheck(this.password1, 'パスワード');
      if (!this.validationErrorPassword1 && this.password2) {
        // 上の欄がバリデチェックOKで、下の欄が入力済みの場合、一致チェックを行う
        this.checkPasswordMatchingCheck();
      }
      this.$emit('watchValidationPassword1', !!this.validationErrorPassword1);
      // 一致チェックを2の方に持たせているので、2の方も1のblurで更新する
      this.$emit('watchValidationPassword2', !!this.validationErrorPassword2);
      this.$emit('blurPassword1');
    },
    onBlurPassword2() {
      if (this.password2 === '' && this.isRequired) {
        // 空欄チェック。必須の場合のみ
        this.validationErrorPassword2 = `パスワード（確認）${GENERAL_VALIDATION_MESSAGE.EMPTY}`;
      } else {
        this.checkPasswordMatchingCheck();
      }
      this.$emit('watchValidationPassword2', !!this.validationErrorPassword2);
      this.$emit('blurPassword2');
    },
    checkPasswordMatchingCheck() {
      if (this.password2 !== this.password1) {
        this.validationErrorPassword2 = SPECIFY_VALIDATION_MESSAGE.PASSWORD.NOT_MATCH;
      } else {
        this.validationErrorPassword2 = '';
      }
    },
    validationCheck(value, areaName) {
      // 必須ではない、かつ、空欄の場合はバリデチェックを通さない
      if (value === '' && !this.isRequired) return undefined;
      if (value === '') {
        // 空欄
        return areaName + GENERAL_VALIDATION_MESSAGE.EMPTY;
      }
      if (!checkWordCount(value, PASSWORD_MIN, Number.MAX_SAFE_INTEGER)) {
        // 最小値チェック
        return generateWordCountError(areaName, PASSWORD_MIN, undefined);
      }
      if (!checkWordCount(value, 1, PASSWORD_MAX)) {
        // 最大値チェック
        return generateWordCountError(areaName, undefined, PASSWORD_MAX);
      }
      if (isHalfWidthKana(value)) {
        return areaName + GENERAL_VALIDATION_MESSAGE.ONLY_HALF_WIDTH_LETTER_NUMBER;
      }
      if (isFullWidth(value)) {
        return areaName + GENERAL_VALIDATION_MESSAGE.ONLY_HALF_WIDTH_LETTER_NUMBER;
      }
      if (!isIncludeNumber(value) || !isIncludeEnglish(value)) {
        // 英か数のどちらかが一文字も入っていない
        return SPECIFY_VALIDATION_MESSAGE.PASSWORD.VALIDATION_ERROR;
      }
      if (this.userName === '') return undefined;
      if (value.indexOf(this.userName) !== -1) {
        // userNameの文字を含んでいる
        // userName未入力の場合は必ずtrueになる
        return SPECIFY_VALIDATION_MESSAGE.PASSWORD.INCLUDE_USER_NAME;
      }
      if (this.emailAccount === '') return undefined;
      if (value.indexOf(this.emailAccount) !== -1) {
        // メールのアカウント部分の文字を含んでいる
        // メールのアカウント部分未入力の場合は必ずtrueになる
        if (this.emailAddress !== undefined) {
          return `パスワードにメールアドレス（${this.emailAddress}）と同じ文字列を含むことはできません。`;
        }
        return SPECIFY_VALIDATION_MESSAGE.PASSWORD.INCLUDE_EMAIL_ACCOUNT;
      }
      return undefined;
    },
    onChangePassword1(value) {
      this.password1 = value;
      this.$emit('change', {
        password1: this.password1,
        password2: this.password2,
      });
    },
    onChangePassword2(value) {
      this.password2 = value;
      this.$emit('change', {
        password1: this.password1,
        password2: this.password2,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'css/contents/common/global';

$label-width-pc: 120px;
$label-width-pc-wide: 124px;
$label-width-has-label-pc: 184px;

.passwordDoubleCheck {
  .labelArea.isPc {
    width: $label-width-pc;
    margin: auto 0;

    &.styleLabelWide {
      width: $label-width-pc-wide;
    }

    &.isHasLabel {
      width: $label-width-has-label-pc;
    }
  }

  &.isPc {
    display: flex;
  }
}

.inputItem {
  &.isPc {
    width: 316px;
  }

  &.hasError,
  &.hasError:focus {
    border: $border-alert;
  }
}

.underInputArea {
  margin-top: 8px;

  &.isPc {
    margin-left: $label-width-has-label-pc;

    &.isNoLabelInPc {
      margin-left: $label-width-pc;
    }

    &.styleLabelWide {
      margin-left: $label-width-pc-wide;
    }
  }

  .validationErrorMessageArea {
    margin-top: 8px;

    .partialErrorComponent {
      &.hasTwoError {
        margin-top: 4px;
      }
    }
  }

  .captionText {
    margin-top: 8px;

    &.hasError.isPc {
      margin-top: 12px;
    }
  }
}
</style>
